/**
 * AI Workspace Styles
 * Centralized styles generator for workspace components
 */

export const getWorkspaceStyles = (colors) => ({
  // Container
  container: {
    backgroundColor: colors.workspaceBg || colors.bgTertiary,
    borderTop: `2px solid ${colors.workspaceBorder || colors.accentPrimary}40`,
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.workspaceBorder || colors.borderColor}`,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  headerIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.accentPrimary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  headerTitleText: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  clearBtn: {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    transition: 'all 0.15s ease',
  },

  // Actions Area
  actionsArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    flexWrap: 'wrap',
  },

  // Action Chip
  actionChip: {
    padding: '8px 16px',
    borderRadius: 20,
    border: 'none',
    backgroundColor: colors.chipBg || colors.bgTertiary,
    color: colors.textPrimary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: 500,
    transition: 'all 0.15s ease',
    position: 'relative',
  },
  actionChipActive: {
    backgroundColor: colors.accentPrimary,
    color: 'white',
  },
  actionChipDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  // Language Dropdown (opens UP)
  languageDropdown: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: 0,
    backgroundColor: colors.bgPrimary,
    borderRadius: 12,
    boxShadow: '0 -8px 30px rgba(0,0,0,0.3)',
    border: `1px solid ${colors.borderColor}`,
    padding: '8px 0',
    minWidth: 240,
    maxHeight: 320,
    overflowY: 'auto',
    zIndex: 100,
  },
  languageHeader: {
    padding: '8px 16px',
    fontSize: 12,
    color: colors.textSecondary,
    fontWeight: 600,
    borderBottom: `1px solid ${colors.borderColor}`,
    marginBottom: 4,
  },
  languageOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  languageOptionDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  languageFlag: {
    fontSize: 20,
    lineHeight: 1,
  },
  languageName: {
    flex: 1,
    fontSize: 14,
    color: colors.textPrimary,
  },
  languageCheck: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: `2px solid ${colors.textTertiary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  },
  languageCheckSelected: {
    backgroundColor: colors.accentPrimary,
    borderColor: colors.accentPrimary,
  },

  // Execute Button
  executeBtn: {
    padding: '8px 20px',
    borderRadius: 20,
    border: 'none',
    backgroundColor: colors.accentPrimary,
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
    marginLeft: 'auto',
    transition: 'all 0.15s ease',
  },
  executeBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  // Results Area
  resultsArea: {
    display: 'flex',
    gap: 12,
    padding: '12px 16px',
    overflowX: 'auto',
    borderTop: `1px solid ${colors.workspaceBorder || colors.borderColor}`,
  },

  // Result Card
  resultCard: {
    flex: '1 1 300px',
    minWidth: 280,
    maxWidth: 400,
    backgroundColor: colors.resultCardBg || colors.bgSecondary,
    borderRadius: 12,
    border: `1px solid ${colors.borderColor}`,
    overflow: 'hidden',
  },
  resultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    backgroundColor: colors.bgTertiary,
    borderBottom: `1px solid ${colors.borderColor}`,
  },
  resultLanguage: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  resultBadge: {
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  resultBody: {
    padding: 14,
    maxHeight: 150,
    overflowY: 'auto',
  },
  resultText: {
    fontSize: 14,
    lineHeight: 1.5,
    color: colors.textPrimary,
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  resultActions: {
    display: 'flex',
    gap: 8,
    padding: '10px 14px',
    borderTop: `1px solid ${colors.borderColor}`,
  },
  resultBtn: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 6,
    border: 'none',
    backgroundColor: colors.bgTertiary,
    color: colors.textPrimary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 500,
    transition: 'all 0.15s ease',
  },
  resultBtnPrimary: {
    backgroundColor: colors.accentPrimary,
    color: 'white',
  },

  // Message Selection Checkbox
  messageCheckbox: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: `2px solid ${colors.textTertiary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  messageCheckboxSelected: {
    backgroundColor: colors.accentPrimary,
    borderColor: colors.accentPrimary,
  },
  messageCheckboxArea: {
    width: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.15s ease',
    flexShrink: 0,
  },
  messageCheckboxAreaVisible: {
    opacity: 1,
  },
  messageHighlight: {
    backgroundColor: `${colors.accentPrimary}15`,
    borderRadius: 8,
    margin: '0 -8px',
    padding: '4px 8px',
  },

  // Loading spinner animation
  loadingSpinner: {
    animation: 'aiWorkspaceSpin 1s linear infinite',
  },

  // Error state
  errorContainer: {
    padding: '12px 16px',
    backgroundColor: `${colors.danger}15`,
    borderTop: `1px solid ${colors.danger}30`,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: colors.danger,
    fontSize: 13,
  },
});

// Extended colors for workspace (merge with theme colors)
export const getWorkspaceColors = (colors, isDark) => ({
  ...colors,
  workspaceBg: isDark ? '#1a2c38' : '#f8fffe',
  workspaceBorder: isDark ? '#2d4a5c' : '#c8e6e0',
  chipBg: isDark ? '#2a3942' : '#e8f5f0',
  chipHover: isDark ? '#3b4a54' : '#d0ebe3',
  resultCardBg: isDark ? '#0d1e26' : '#ffffff',
  selectionHighlight: isDark ? 'rgba(0, 168, 132, 0.15)' : 'rgba(0, 168, 132, 0.1)',
  // Role colors — used to differentiate operator (you) vs customer (contact)
  // when messages are selected for the AI.
  roleOperator: isDark ? '#60A5FA' : '#3B82F6',
  roleCustomer: colors.accentPrimary,
});

export default { getWorkspaceStyles, getWorkspaceColors };